/**
 * /app/$tenantSlug/approval-delegation — "Out of office" (wave 2).
 *
 * Self-service approval delegation: an approver (manager, team lead with
 * reports, or HR) names a colleague to cover their approval queue for a
 * date range. While it is live the delegate is notified instead of them,
 * sees the PENDING requests they would approve, and can decide them. The
 * backend is the boundary (the delegator is always the signed-in user;
 * a caller who approves nothing is refused).
 *
 * HR admins also get the workspace's reminder-escalation setting here:
 * after N days a still-pending request of any approval type (leave, WFH,
 * expense, regularization, timesheet, travel) is escalated once to the
 * skip-level manager and HR (0 turns it off).
 *
 * Inbox surface: listed in lib/surfaces.ts (INBOX item + INBOX_PREFIXES).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { CalendarClock, Loader2, Plane, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { InboxPage } from "@/components/inbox/inbox-page";
import { EmptyState } from "@/components/shared/empty-state";
import { EmployeeDirectoryPicker } from "@/components/shared/employee-directory-picker";
import { useDirectory } from "@/hooks/use-directory";
import { reportsTo } from "@/lib/reporting-line";
import {
  useApprovalSettings,
  useCreateDelegation,
  useDelegations,
  useRevokeDelegation,
  useUpdateApprovalSettings,
} from "@/hooks/use-approval-routing";
import type { ApprovalDelegation } from "@/lib/api/approvals";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { fmtDateRange } from "@/lib/leave-format";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/approval-delegation",
)({
  component: ApprovalDelegationRoute,
});

const STATUS_LABEL: Record<ApprovalDelegation["status"], string> = {
  active: "Active",
  upcoming: "Upcoming",
  ended: "Ended",
  revoked: "Revoked",
};

function StatusPill({ status }: Readonly<{ status: ApprovalDelegation["status"] }>) {
  return (
    <Badge variant={status === "active" ? "default" : "outline"}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function today(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function ApprovalDelegationRoute() {
  const { user } = useAuth();
  const q = useDelegations();
  const hr = isHRAdmin(user?.role);

  return (
    <InboxPage
      title="Out of Office"
      description="Hand your approval queue to a colleague while you're away."
    >
      <div className="space-y-5">
        {q.isPending && (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load your delegations</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}
        {q.data && (
          <>
            {q.data.covering.length > 0 && (
              <CoveringCard rows={q.data.covering} />
            )}
            {q.data.can_delegate ? (
              <>
                <DelegateForm myId={user?.user_id} />
                <MyDelegations rows={q.data.mine} />
              </>
            ) : (
              <EmptyState
                icon={UserCheck}
                title="Nothing to Delegate"
                description="You don't approve anyone's requests, so there's no approval queue to hand over. Managers, team leads with reports and HR can delegate here."
              />
            )}
          </>
        )}
        {hr && <EscalationCard />}
      </div>
    </InboxPage>
  );
}

function CoveringCard({ rows }: Readonly<{ rows: ApprovalDelegation[] }>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">You're Covering For</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y text-sm">
          {rows.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <span className="font-medium">
                  {d.delegator_name ?? `User #${d.delegator_id}`}
                </span>{" "}
                · {fmtDateRange(d.starts_on, d.ends_on)}
              </span>
              <StatusPill status={d.status} />
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Their pending requests appear in your Approvals inbox while the
          delegation is active.
        </p>
      </CardContent>
    </Card>
  );
}

function DelegateForm({ myId }: Readonly<{ myId: number | undefined }>) {
  const create = useCreateDelegation();
  const dir = useDirectory();
  const [delegateId, setDelegateId] = useState<number | undefined>();
  const [startsOn, setStartsOn] = useState(today());
  const [endsOn, setEndsOn] = useState(today());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Bumped after a save to remount the picker: a controlled Radix Select
  // set back to `undefined` keeps SHOWING the last pick, so the form
  // looked filled while its state was empty and a second Save said
  // "Choose who should cover your approvals." (2026-10 QA #10).
  const [formKey, setFormKey] = useState(0);

  const invalidRange = endsOn < startsOn;
  // A delegate never decides a request from someone above them in their
  // reporting line (server rule) — so a delegate from your own team cannot
  // cover YOUR requests or your managers'; those stay with you and HR.
  const delegateIsReport = reportsTo(dir.byId, delegateId, myId);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!delegateId) {
      setError("Choose who should cover your approvals.");
      return;
    }
    try {
      await create.mutateAsync({
        delegate_id: delegateId,
        starts_on: startsOn,
        ends_on: endsOn,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      toast.success(
        `Delegation saved — ${dir.label(delegateId, undefined, false)} has been told they're covering your approvals.`,
      );
      setDelegateId(undefined);
      setStartsOn(today());
      setEndsOn(today());
      setReason("");
      setFormKey((k) => k + 1);
    } catch (err) {
      setError(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Delegate My Approvals</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
          <div className="space-y-1.5">
            <Label>Covered By</Label>
            <EmployeeDirectoryPicker
              key={formKey}
              value={delegateId}
              onChange={setDelegateId}
              excludeId={myId}
              placeholder="Choose a colleague"
            />
            {delegateIsReport && (
              <p className="text-xs text-amber-700" role="status">
                {dir.label(delegateId, undefined, false)} reports to you. While
                covering they can decide your team&apos;s requests, but never a
                request from you or anyone above them — those stay with you
                and HR.
              </p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="deleg-from">From</Label>
              <Input
                id="deleg-from"
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deleg-to">To (Inclusive)</Label>
              <Input
                id="deleg-to"
                type="date"
                value={endsOn}
                min={startsOn}
                onChange={(e) => setEndsOn(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deleg-reason">Note (Optional)</Label>
            <Textarea
              id="deleg-reason"
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. On annual leave"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Up to 90 days. Your colleague is notified of new requests in
            your place and can approve or reject them; you can still decide
            them yourself.
          </p>
          {(error || invalidRange) && (
            <Alert variant="destructive">
              <AlertDescription>
                {invalidRange ? "The end date must be on or after the start date." : error}
              </AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={create.isPending || invalidRange} className="gap-1.5">
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plane className="h-4 w-4" />
            )}
            Save Delegation
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function MyDelegations({ rows }: Readonly<{ rows: ApprovalDelegation[] }>) {
  const revoke = useRevokeDelegation();
  const [revokeId, setRevokeId] = useState<number | null>(null);

  async function onConfirm() {
    if (revokeId == null) return;
    try {
      await revoke.mutateAsync(revokeId);
      toast.success("Delegation revoked.");
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setRevokeId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">My Delegations</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title="No Delegations Yet"
            description="When you set one up it appears here, with the last 30 days of history."
          />
        ) : (
          <ul className="divide-y text-sm">
            {rows.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <p className="font-medium">
                    {d.delegate_name ?? `User #${d.delegate_id}`}
                  </p>
                  <p className="text-muted-foreground">
                    {fmtDateRange(d.starts_on, d.ends_on)}
                    {d.reason ? ` · ${d.reason}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={d.status} />
                  {(d.status === "active" || d.status === "upcoming") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setRevokeId(d.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AlertDialog open={revokeId !== null} onOpenChange={(o) => !o && setRevokeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this delegation?</AlertDialogTitle>
            <AlertDialogDescription>
              Your colleague stops covering your approvals immediately and
              new requests come back to you.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Keep It</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void onConfirm();
              }}
              disabled={revoke.isPending}
              className="gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoke.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function EscalationCard() {
  const q = useApprovalSettings(true);
  const save = useUpdateApprovalSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? (q.data ? String(q.data.escalation_days) : "");
  const n = Number(value);
  const invalid = value === "" || !Number.isInteger(n) || n < 0 || n > 30;

  async function onSave() {
    try {
      await save.mutateAsync({ escalation_days: n });
      toast.success(
        n === 0 ? "Escalation turned off." : `Escalation set to ${n} ${n === 1 ? "day" : "days"}.`,
      );
      setDraft(null);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Reminder Escalation (Workspace Setting)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Approvers get one daily digest of everything waiting on them —
          leave, WFH, expense claims, attendance corrections, timesheets
          and travel pre-approvals. A request still pending after this many
          days is escalated once to the approver&apos;s own manager and to
          HR. Set 0 to turn escalation off.
        </p>
        {q.isPending && <Skeleton className="h-9 w-40" />}
        {q.isError && (
          <Alert variant="destructive">
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}
        {q.data && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="esc-days">Escalate After (Days)</Label>
                <Input
                  id="esc-days"
                  type="number"
                  min={0}
                  max={30}
                  className="w-32"
                  value={value}
                  aria-invalid={invalid}
                  aria-describedby="esc-days-help"
                  onChange={(e) => setDraft(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={() => void onSave()} disabled={invalid || save.isPending} className="gap-1.5">
                {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
            {/* Say why Save is disabled (2026-10 QA #17 — 99 just greyed
                the button out). */}
            <p
              id="esc-days-help"
              className={invalid ? "text-xs text-destructive" : "text-xs text-muted-foreground"}
            >
              {invalid
                ? "Enter a whole number of days from 0 to 30 (0 turns escalation off)."
                : "0 to 30 days; 0 turns escalation off."}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
